import { rng } from './fx';
import type { CssKeyframe } from './pose';
import type { Label, PresetParam, PresetParams } from './presets';
import type { Box } from './transitions';

/**
 * Camera moves of the whole composition (push-in, pan, shake…). There is no camera element to
 * move, so a move is expressed per top-level layer: zoom and roll around the canvas centre become
 * that layer's own `translate` + `rotate` + `scale`, composited additively on top of everything
 * else the layer does. Layers therefore move as one picture, and their own animations keep
 * playing inside the moving frame.
 */

export interface CameraPose {
  /** Zoom (1 = none). */
  k?: number;
  /** Pan, canvas px. */
  x?: number;
  y?: number;
  /** Roll, degrees. */
  rot?: number;
  /** Defocus, px at a 1080 px canvas. */
  blur?: number;
  offset?: number;
  easing?: string;
}

export interface CameraContext {
  canvas: { width: number; height: number };
  params: PresetParams;
  seed: number;
}

export interface CameraPreset {
  id: string;
  label: Label;
  duration: number;
  easing: string;
  params: PresetParam[];
  poses: (ctx: CameraContext) => CameraPose[];
}

const lbl = (en: string, uk: string, pl: string, ro: string, es: string, ru: string): Label => [
  en,
  uk,
  pl,
  ro,
  es,
  ru,
];

const P_INTENSITY: PresetParam = {
  key: 'intensity',
  type: 'number',
  default: 1,
  min: 0.2,
  max: 3,
  step: 0.1,
  label: lbl('Intensity', 'Сила', 'Siła', 'Intensitate', 'Intensidad', 'Сила'),
};
const P_DIRECTION: PresetParam = {
  key: 'direction',
  type: 'direction',
  default: 'left',
  label: lbl('Direction', 'Напрямок', 'Kierunek', 'Direcție', 'Dirección', 'Направление'),
};

const intensity = (ctx: CameraContext) => {
  const v = Number(ctx.params.intensity);
  return Number.isFinite(v) ? Math.max(0.1, Math.min(3, v)) : 1;
};

export const CAMERA_PRESETS: CameraPreset[] = [
  {
    id: 'push-in',
    label: lbl('Push in', 'Наїзд', 'Najazd', 'Apropiere', 'Acercamiento', 'Наезд'),
    duration: 5000,
    easing: 'cubic-bezier(0.37, 0, 0.63, 1)',
    params: [P_INTENSITY],
    poses: (ctx) => [{ k: 1 }, { k: 1 + 0.1 * intensity(ctx) }],
  },
  {
    id: 'pull-out',
    label: lbl('Pull out', 'Відʼїзд', 'Odjazd', 'Depărtare', 'Alejamiento', 'Отъезд'),
    duration: 5000,
    easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
    params: [P_INTENSITY],
    poses: (ctx) => [{ k: 1 + 0.16 * intensity(ctx) }, { k: 1 }],
  },
  {
    id: 'zoom-burst',
    label: lbl(
      'Zoom burst',
      'Ривок камери',
      'Szarpnięcie',
      'Zoom brusc',
      'Zoom brusco',
      'Рывок камеры',
    ),
    duration: 700,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    params: [P_INTENSITY],
    poses: (ctx) => [
      { k: 1 + 0.4 * intensity(ctx), blur: 10 * intensity(ctx) },
      { k: 1, blur: 0 },
    ],
  },
  {
    id: 'punch',
    label: lbl('Beat punch', 'Удар у ритм', 'Uderzenie', 'Lovitură', 'Golpe', 'Удар в ритм'),
    duration: 450,
    easing: 'linear',
    params: [P_INTENSITY],
    poses: (ctx) => [
      { k: 1, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
      { k: 1 + 0.08 * intensity(ctx), offset: 0.25, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' },
      { k: 1 },
    ],
  },
  {
    id: 'pan',
    label: lbl('Pan', 'Панорама', 'Panorama', 'Panoramare', 'Paneo', 'Панорама'),
    duration: 5000,
    easing: 'cubic-bezier(0.37, 0, 0.63, 1)',
    params: [P_DIRECTION, P_INTENSITY],
    poses: (ctx) => {
      const i = intensity(ctx);
      const k = 1 + 0.1 * i;
      const d = ctx.params.direction;
      const mx = ((k - 1) / 2) * ctx.canvas.width * 0.85;
      const my = ((k - 1) / 2) * ctx.canvas.height * 0.85;
      const s = d === 'right' || d === 'down' ? 1 : -1;
      const horizontal = d !== 'up' && d !== 'down';
      return [
        { k, x: horizontal ? -s * mx : 0, y: horizontal ? 0 : -s * my },
        { k, x: horizontal ? s * mx : 0, y: horizontal ? 0 : s * my },
      ];
    },
  },
  {
    id: 'drift',
    label: lbl('Handheld drift', 'Дрейф', 'Dryf', 'Derivă', 'Deriva', 'Дрейф'),
    duration: 5000,
    easing: 'linear',
    params: [P_INTENSITY],
    poses: (ctx) => {
      const i = intensity(ctx);
      const r = rng(ctx.seed);
      const m = Math.min(ctx.canvas.width, ctx.canvas.height) * 0.012 * i;
      const e = 'cubic-bezier(0.45, 0, 0.55, 1)';
      return [0, 1, 2, 3].map((n) => ({
        k: 1.01 + 0.012 * i * (n / 3),
        x: (r() - 0.5) * 2 * m,
        y: (r() - 0.5) * 2 * m,
        rot: (r() - 0.5) * 1.6 * i,
        easing: e,
      }));
    },
  },
  {
    id: 'shake',
    label: lbl('Impact shake', 'Струс', 'Wstrząs', 'Scuturare', 'Sacudida', 'Тряска'),
    duration: 600,
    easing: 'linear',
    params: [P_INTENSITY],
    poses: (ctx) => {
      const i = intensity(ctx);
      const r = rng(ctx.seed);
      const a = Math.min(ctx.canvas.width, ctx.canvas.height) * 0.025 * i;
      const n = 9;
      return Array.from({ length: n + 1 }, (_, s) => {
        const decay = 1 - s / n;
        const sign = s % 2 ? -1 : 1;
        return s === n
          ? { k: 1, x: 0, y: 0, rot: 0 }
          : {
              k: 1 + 0.03 * i * decay,
              x: sign * a * decay * (0.5 + r() * 0.5),
              y: (r() - 0.5) * a * decay,
              rot: sign * 1.2 * i * decay * r(),
              easing: 'ease-in-out',
            };
      });
    },
  },
  {
    id: 'dutch',
    label: lbl('Dutch tilt', 'Нахил', 'Przechył', 'Înclinare', 'Inclinación', 'Наклон'),
    duration: 5000,
    easing: 'cubic-bezier(0.37, 0, 0.63, 1)',
    params: [P_INTENSITY],
    poses: (ctx) => {
      const i = intensity(ctx);
      return [
        { k: 1.02, rot: 0 },
        { k: 1.02 + 0.1 * i, rot: -4 * i },
      ];
    },
  },
  {
    id: 'roll-in',
    label: lbl(
      'Roll in',
      'Вліт з поворотом',
      'Wlot z obrotem',
      'Intrare cu rotire',
      'Entrada con giro',
      'Влёт с поворотом',
    ),
    duration: 900,
    easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    params: [P_INTENSITY],
    poses: (ctx) => {
      const i = intensity(ctx);
      return [
        { k: 1 + 0.5 * i, rot: 10 * i, blur: 8 * i },
        { k: 1, rot: 0, blur: 0 },
      ];
    },
  },
  {
    id: 'focus',
    label: lbl(
      'Focus pull',
      'Наведення фокуса',
      'Ostrzenie',
      'Focalizare',
      'Enfoque',
      'Наводка фокуса',
    ),
    duration: 1200,
    easing: 'cubic-bezier(0.33, 1, 0.68, 1)',
    params: [P_INTENSITY],
    poses: (ctx) => {
      const i = intensity(ctx);
      return [
        { k: 1.06, blur: 16 * i },
        { k: 1, blur: 0 },
      ];
    },
  },
];

export const CAMERA_PRESET_MAP = new Map(CAMERA_PRESETS.map((p) => [p.id, p]));

const r2 = (v: number) => Math.round(v * 100) / 100;

/** Keyframes for one top-level layer whose layout box is `box`. */
export function cameraFrames(
  poses: CameraPose[],
  box: Box,
  canvas: { width: number; height: number },
  blurScale: number,
): CssKeyframe[] {
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const ox = box.x + box.width / 2;
  const oy = box.y + box.height / 2;
  const hasRot = poses.some((p) => p.rot);
  const hasBlur = poses.some((p) => p.blur);
  return poses.map((p) => {
    const k = p.k ?? 1;
    const a = ((p.rot ?? 0) * Math.PI) / 180;
    // Where the layer's centre goes when the canvas is zoomed and rolled around its centre
    const vx = (ox - cx) * k;
    const vy = (oy - cy) * k;
    const tx = cx + vx * Math.cos(a) - vy * Math.sin(a) - ox + (p.x ?? 0);
    const ty = cy + vx * Math.sin(a) + vy * Math.cos(a) - oy + (p.y ?? 0);
    const f: CssKeyframe = {
      translate: `${r2(tx)}px ${r2(ty)}px`,
      scale: String(r2(k * 10000) / 10000),
    };
    if (hasRot) f.rotate = `${r2(p.rot ?? 0)}deg`;
    if (hasBlur) f.filter = `blur(${r2((p.blur ?? 0) * blurScale)}px)`;
    if (p.offset !== undefined) f.offset = p.offset;
    if (p.easing) f.easing = p.easing;
    return f;
  });
}
